import { useParams } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useDeleteRecipeTag } from '../api/recipe-tag-mutations';
import { RecipeTag } from '../types/recipe-tag-types';

interface DeleteRecipeTagDialogProps {
  tag: RecipeTag | null;
  onClose: () => void;
}

export function DeleteRecipeTagDialog({
  tag,
  onClose,
}: DeleteRecipeTagDialogProps) {
  const { tenantId } = useParams({
    from: '/_authenticated/$tenantId/settings/recipe-tags',
  });

  const deleteTag = useDeleteRecipeTag();

  const handleDelete = async () => {
    if (!tag) return;

    try {
      await deleteTag.mutateAsync({
        id: tag.id,
        organisation_id: tenantId,
      });
      onClose();
    } catch {
      toast.error('Failed to delete tag');
    }
  };

  if (!tag) return null;

  return (
    <Dialog open={!!tag} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-destructive" />
            Delete Recipe Tag
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the tag &quot;{tag.title}&quot;?
            This action cannot be undone and will remove the tag from all
            recipes that use it.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTag.isPending}
          >
            {deleteTag.isPending ? 'Deleting...' : 'Delete Tag'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
